import {z} from 'zod';

import {useRef, useState} from 'react';
import type {DeepPartial} from 'react-hook-form';

import {equals, isNil, isNotNil} from 'ramda';

import {
  FINANCING_ENDING_BULK_ACTION_CODE,
  financingEndingApi,
  type FinancingEndingEmailSourceRequestBody,
} from '@dms/api/sales';
import type {UploadedCustomerMessageFile} from '@dms/shared';

import {type ApiError, HTTP_STATUS_CODES, useOnMount} from 'shared/web';

import type {FinancingEndingEditableEmailFormValues} from '../utils/FinancingEndingEditableEmailFormSchema';
import {
  type FinancingEndingEmailDraft,
  getFinancingEndingEmailDraft,
} from '../utils/getFinancingEndingEmailDraft';

export type FinancingEndingEditableEmailSubmitInput = {
  recordIds: readonly string[];
  values: DeepPartial<FinancingEndingEditableEmailFormValues>;
  attachments: readonly Pick<UploadedCustomerMessageFile, 'fileId' | 'shouldConvertToPdf'>[];
};

export type FinancingEndingEditableEmailSubmitResult =
  | {status: 'submitted'; bulkOperationId: string}
  | {status: 'incomplete'}
  | {status: 'alreadySubmitting'}
  | {status: 'alreadySubmitted'}
  | {status: 'abandoned'}
  | {status: 'prepareFailed'; error: ApiError}
  | {status: 'submitRefused'; error: ApiError}
  | {status: 'submitOutcomeUnknown'; error: ApiError};

type PreparedSource = {
  source: Readonly<FinancingEndingEmailSourceRequestBody>;
  sourceId: string;
};

type SubmitSnapshot = {
  draft: FinancingEndingEmailDraft;
  recordIds: string[];
  attachmentFileIds: string[];
  pdfAttachmentFileIds: string[];
};

const takeSnapshot = (input: FinancingEndingEditableEmailSubmitInput): SubmitSnapshot | null => {
  const draft = getFinancingEndingEmailDraft(input.values);

  if (isNil(draft)) {
    return null;
  }

  return {
    draft,
    recordIds: [...input.recordIds],
    attachmentFileIds: input.attachments.map((file) => file.fileId),
    pdfAttachmentFileIds: input.attachments
      .filter((file) => file.shouldConvertToPdf)
      .map((file) => file.fileId),
  };
};

// An answer below 500 means the server did not accept the operation. Without an answer, or with a
// server error, the operation may have been created, so a resend may send the e-mails twice.
const isRefusal = (error: ApiError) =>
  'status' in error &&
  typeof error.status === 'number' &&
  error.status >= HTTP_STATUS_CODES.BAD_REQUEST &&
  error.status < HTTP_STATUS_CODES.INTERNAL_ERROR;

// The server cannot use the prepared source any more; only a new source can be sent.
const SOURCE_NOT_USABLE_ERROR_CODE = 'DMS-BC-FE-0022';

const errorCodesSchema = z.object({
  data: z.object({errors: z.array(z.object({code: z.string()}))}),
});

const isSourceNotUsable = (error: ApiError) => {
  const result = errorCodesSchema.safeParse(error);
  return (
    result.success &&
    result.data.data.errors.some((item) => item.code === SOURCE_NOT_USABLE_ERROR_CODE)
  );
};

/**
 * Sends the editable financing-ending e-mail: first the text as a source, then the bulk action with
 * the source id, the selected records, the mailbox and the attachments. The text never goes into the
 * bulk params.
 *
 * Everything is read once when `submit` is called, so a later edit does not change this send. The
 * source id is kept while the text, template and locale stay the same, so a retry after a refused
 * or unanswered submit sends the same source again; changed text, or a refusal that says the source
 * cannot be used, prepares a new source. The hook never resends by itself. A second `submit` while
 * one runs, or after one was accepted, is refused. After the unmount the bulk action is not started,
 * but one already sent is not taken back.
 */
export function useSubmitFinancingEndingEditableEmail() {
  const [prepareSource] = financingEndingApi.usePrepareFinancingEndingEmailSourceMutation();
  const [submitBulkAction] = financingEndingApi.useSubmitFinancingEndingBulkActionMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const isSubmittingRef = useRef(false);
  const isSubmittedRef = useRef(false);
  const preparedSourceRef = useRef<PreparedSource | null>(null);
  const isMountedRef = useRef(true);

  useOnMount(
    () => {
      isMountedRef.current = true;
    },
    () => {
      isMountedRef.current = false;
    }
  );

  const getSourceId = (
    source: Readonly<FinancingEndingEmailSourceRequestBody>
  ): Promise<{sourceId: string} | {error: ApiError}> => {
    const prepared = preparedSourceRef.current;

    if (isNotNil(prepared) && equals(prepared.source, source)) {
      return Promise.resolve({sourceId: prepared.sourceId});
    }

    preparedSourceRef.current = null;

    // Two handlers, not `.catch`: only a request error is a failed preparation.
    return prepareSource({financingEndingEmailSourceRequestBody: source})
      .unwrap()
      .then(
        ({sourceId}) => {
          preparedSourceRef.current = {source, sourceId};
          return {sourceId};
        },
        (prepareError: ApiError) => ({error: prepareError})
      );
  };

  const send = async (
    snapshot: SubmitSnapshot
  ): Promise<FinancingEndingEditableEmailSubmitResult> => {
    const {draft, recordIds, attachmentFileIds, pdfAttachmentFileIds} = snapshot;
    const prepared = await getSourceId(draft.source);

    if ('error' in prepared) {
      return {status: 'prepareFailed', error: prepared.error};
    }

    if (!isMountedRef.current) {
      return {status: 'abandoned'};
    }

    // Two handlers, not `.catch`: only a request error is an unknown or refused submit, never a
    // throw after the server accepted the operation.
    return submitBulkAction({
      actionCode: FINANCING_ENDING_BULK_ACTION_CODE,
      submitBulkActionRequestBody: {
        recordIds,
        params: {
          sourceId: prepared.sourceId,
          templateId: draft.source.templateId,
          emailCredentialsId: draft.emailCredentialsId,
          attachmentFileIds,
          pdfAttachmentFileIds,
        },
      },
    })
      .unwrap()
      .then(
        ({bulkOperationId}): FinancingEndingEditableEmailSubmitResult => {
          preparedSourceRef.current = null;
          return {status: 'submitted', bulkOperationId};
        },
        (submitError: ApiError): FinancingEndingEditableEmailSubmitResult => {
          if (!isRefusal(submitError)) {
            return {status: 'submitOutcomeUnknown', error: submitError};
          }
          if (isSourceNotUsable(submitError)) {
            preparedSourceRef.current = null;
          }
          return {status: 'submitRefused', error: submitError};
        }
      );
  };

  const submit = async (
    input: FinancingEndingEditableEmailSubmitInput
  ): Promise<FinancingEndingEditableEmailSubmitResult> => {
    if (isSubmittingRef.current) {
      return {status: 'alreadySubmitting'};
    }

    if (isSubmittedRef.current) {
      return {status: 'alreadySubmitted'};
    }

    const snapshot = takeSnapshot(input);

    if (isNil(snapshot)) {
      return {status: 'incomplete'};
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setError(null);

    const result = await send(snapshot);

    isSubmittedRef.current = result.status === 'submitted';
    isSubmittingRef.current = false;
    setIsSubmitting(false);
    setError('error' in result ? result.error : null);

    return result;
  };

  return {isSubmitting, error, submit};
}
